import { CollectionRecord } from "@/shared/domain/collections";
import { DatasetRecord } from "@/shared/domain/datasets";
import { FileRecord } from "@/shared/domain/files/FileRecord";
import type { QueryTarget } from "@/shared/domain/query";
import type { SessionRecord } from "@/shared/domain/sessions";
import type { ViewDefinitionV1, ViewRecord } from "@/shared/domain/views";

/**
 * Fields common to every viewing context.
 *
 * `orgId` is the org that owns whatever the part describes. A part leaves it
 * `undefined` when it carries no ownership information — either because its
 * record hasn't loaded yet (resource ids in the URL don't encode their owning
 * org; only the fetched record does) or because the part is about something
 * other than ownership (e.g. a dataset's selected-files part). The org switch
 * is derived by deduping `orgId` across all registered parts
 * ({@link ViewingContextService.getViewingOrgId}), so an `undefined` part
 * simply says nothing and is ignored.
 */
export interface BaseViewingContext {
  orgId?: string;
}

interface DatasetDataSource {
  type: "dataset";
  id: string;
  record?: DatasetRecord;
}
interface FileDataSource {
  type: "file";
  id: string;
  record?: FileRecord;
}
export type VisualizerDataSource = DatasetDataSource | FileDataSource;

/**
 * Key inside {@link VisualizerViewingContext.state} holding the earliest
 * timestamp across everything loaded in the workspace, as a nanosecond decimal
 * string (epoch nanoseconds exceed `Number.MAX_SAFE_INTEGER`). The same
 * instant the visualizer's "Elapsed" display counts from, and therefore the
 * t=0 an AI thread counts from while it is set to follow the visualizer.
 *
 * On the reactive channel rather than the live one (`CURRENT_TIME_NS_FIELD`
 * in ./liveVisualizerState) because it changes only when the user loads
 * different files, and because consumers have to re-render when it does.
 *
 * The state bag is untyped, so every reader validates: `BigInt` reads "" as
 * `0n` and accepts negatives, either of which would offer a 1970 instant as
 * the workspace start.
 */
export const EARLIEST_TIME_NS_FIELD = "earliest_time_ns";

/**
 * Viewing context for the visualizer page
 */
export interface VisualizerViewingContext extends BaseViewingContext {
  dataSources: VisualizerDataSource[];
  state: Record<string, unknown>;
}

/**
 * Viewing context for the dataset detail page
 */
export interface DatasetViewingContext extends BaseViewingContext {
  datasetId: string;
  record?: DatasetRecord;
  selectedFiles?: FileRecord[];
}

/**
 * Viewing context for the file detail page
 */
export interface FileViewingContext extends BaseViewingContext {
  fileId: string;
  record?: FileRecord;
}

/**
 * Viewing context for the collection detail page
 */
export interface CollectionViewingContext extends BaseViewingContext {
  collectionId: string;
  record?: CollectionRecord;
}

/**
 * Viewing context for the device detail page.
 *
 * Carries no record: a device's ID is user-chosen and is the only name the
 * device has, so the ID alone labels it everywhere a consumer displays it —
 * no fetch needed. `orgId` is likewise always absent (see
 * {@link ViewingContextMap}).
 */
export interface DeviceViewingContext extends BaseViewingContext {
  deviceId: string;
}

/**
 * Viewing context for the session detail page.
 *
 * `record` supplies the session's name for display; the id alone reaches the
 * agent while it loads. No `orgId`, for the same reason as `device` (see
 * {@link ViewingContextMap}).
 */
export interface SessionViewingContext extends BaseViewingContext {
  sessionId: string;
  record?: SessionRecord;
}

/**
 * Viewing context for a resource table: the six list pages.
 *
 * Carries the table's live state as a View definition whether or not a View
 * is loaded, so the agent can describe ad-hoc filters and "save what I'm
 * looking at as a View" names exactly what is on screen. `loadedView` is the
 * View applied to the table, when there is one.
 *
 * Agent-only, like `device`: a list page shows the caller's active org, so
 * there is never a switch to offer, and it registers no `orgId`.
 */
export interface ResourceTableViewingContext extends BaseViewingContext {
  target: QueryTarget;
  definition: ViewDefinitionV1;
  loadedView?: ViewRecord;
}

/**
 * Maps viewing context source names to their corresponding context types.
 * This enables type-safe registration of viewing contexts where the value
 * type is enforced based on the source name.
 *
 * Two independent consumers read what pages register here, and they want
 * different things. Membership satisfies whichever one applies — a page
 * belongs if *either* consumer has use for it:
 *
 * 1. **AI Chat** reads the merged context and forwards the entities it names
 *    to the agent, so the user can say "this dataset" instead of pasting an
 *    id. It wants an entity id, so a page it cares about registers a typed
 *    interface (`datasetId`, `collectionId`, ...) rather than a plain
 *    {@link BaseViewingContext}.
 * 2. **The org switch** dedupes `orgId` across the raw parts
 *    ({@link ViewingContextService.getViewingOrgId}) to offer switching into
 *    the org that owns what you're looking at. It reads nothing but `orgId`,
 *    so a page that exists only for the switch registers a plain
 *    {@link BaseViewingContext} and needs no service branch.
 *
 * `action`/`invocation` are switch-only today: they carry no id the agent
 * currently acts on. `agent`/`thread` are switch-only for a second reason —
 * their read routes look the resource up by its platform-unique id and
 * authorize against the record's own org (with a Roboto-admin bypass), so a
 * system user resolves them regardless of active org, a cross-org view the
 * switch should offer.
 *
 * `device` is the mirror image: useful to the agent, useless to the switch.
 * Its read route looks the device up by `(id, caller's active org)`, so a
 * cross-org id simply 404s and you must already be in the owning org to have
 * loaded the page — there is never a switch to offer. It therefore registers
 * a `deviceId` and no `orgId` at all. `session` registers the same way, for
 * the same reason. `trigger` pages share the lookup semantics and remain absent
 * only because neither consumer needs them yet; add them when one does.
 *
 * `resourceTable` is agent-only for the same reason as `device`: a list page
 * always shows the active org.
 */
export interface ViewingContextMap {
  dataset: DatasetViewingContext;
  file: FileViewingContext;
  visualizer: VisualizerViewingContext;
  collection: CollectionViewingContext;
  device: DeviceViewingContext;
  session: SessionViewingContext;
  resourceTable: ResourceTableViewingContext;
  action: BaseViewingContext;
  agent: BaseViewingContext;
  invocation: BaseViewingContext;
  thread: BaseViewingContext;
}

export type ViewingContextSourceName = keyof ViewingContextMap;
export type ViewingContextSource = ViewingContextMap[ViewingContextSourceName];

export type ViewingContext = Partial<ViewingContextMap>;
